import React, { useEffect, useMemo, useRef, useState } from "react";
import { format, parseISO, subDays, differenceInCalendarDays } from "date-fns";
import { mdy, mdyDow } from "@/lib/dateFmt";
import { Users2, X, Moon, AlertTriangle, ShieldAlert, Save, Building2 } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { colorForShift } from "@/lib/shifts";
import { deptColorFor, UNASSIGNED_LABEL } from "@/lib/departments";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const toMin = (s) => {
  if (!s || typeof s !== "string") return null;
  const [h, m] = s.split(":").map(Number);
  return Number.isNaN(h) || Number.isNaN(m) ? null : h * 60 + m;
};
const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const SNAP = 10;
const TIMELINE_WIDTH = 1440;   // 1px per minute

// Paid hours for a shift window: >6 raw hours ⇒ auto 0.5h lunch deducted
const paidHours = (s, e) => {
  const raw = (e - s) / 60;
  return raw > 6 ? +(raw - 0.5).toFixed(2) : +raw.toFixed(2);
};

// Turn one shift-code window into 30-min bucket indices it covers on THIS day.
// Handles overnights (splits into today's tail + tomorrow's head).
function bucketsForCode(codeMeta, dayOffsetVsSelected) {
  // dayOffsetVsSelected: 0 = same day, -1 = code starts on prev day and wraps into selected
  const s = toMin(codeMeta?.start), e = toMin(codeMeta?.end);
  if (s == null || e == null || s === e) return [];
  const overnight = e <= s;
  const out = [];
  const push = (from, to) => {
    for (let i = Math.floor(from / 30); i < Math.ceil(to / 30); i++) out.push(i);
  };
  if (dayOffsetVsSelected === 0) {
    // Shift starts today
    if (overnight) push(s, 1440);
    else push(s, e);
  } else if (dayOffsetVsSelected === -1) {
    // Shift started yesterday and wraps into today
    if (overnight) push(0, e);
  }
  return out;
}

function segmentsForDay({ iso, assignments, employees, roster, codeMap }) {
  if (!iso) return [];
  const empById = new Map();
  (employees || []).forEach(e => empById.set(e.id, e));
  (roster || []).forEach(e => { if (e.id && !empById.has(e.id)) empById.set(e.id, e); });
  const prevIso = format(subDays(parseISO(iso), 1), "yyyy-MM-dd");
  const segs = [];
  for (const a of (assignments || [])) {
    // Fallback to shift_code defaults for legacy assignments that lack explicit times.
    const codeMeta = codeMap?.get(a.shift_code);
    const startStr = a.start_time || codeMeta?.start || null;
    const endStr = a.end_time || codeMeta?.end || null;
    const s = toMin(startStr), e = toMin(endStr);
    if (s == null || e == null || s === e) continue;
    const emp = empById.get(a.employee_id) || { id: a.employee_id, name: a.employee_name || "Employee", avatar: null };
    const overnight = e <= s;
    const gS = a.long_lunch_start ? toMin(a.long_lunch_start) : null;
    const gE = a.long_lunch_end   ? toMin(a.long_lunch_end)   : null;
    const longLunch = (gS != null && gE != null) ? { start: gS, end: gE } : null;
    const deptName = (codeMeta?.department || "").trim() || UNASSIGNED_LABEL;
    const deptMeta = deptColorFor(deptName === UNASSIGNED_LABEL ? null : deptName, codeMeta?.department_color);
    const common = {
      id: a.id, code: a.shift_code, label: a.shift_label,
      facility_id: a.facility_id, date: a.date, emp,
      startText: startStr, endText: endStr,
      deptName, deptMeta,
    };
    if (a.date === iso) {
      segs.push({
        ...common, key: `${a.id}-1`,
        segStart: s, segEnd: overnight ? 1440 : e, longLunch,
        fromPrev: false, origStart: s, origEnd: overnight ? 1440 : e,
        editable: !a.external,
      });
    }
    if (overnight && a.date === prevIso) {
      segs.push({
        ...common, key: `${a.id}-2`,
        segStart: 0, segEnd: e, longLunch: null,
        fromPrev: true, origStart: 0, origEnd: e,
        editable: false, // don't edit yesterday's tail from today
      });
    }
  }
  segs.sort((a, b) => {
    if (a.deptName !== b.deptName) {
      // Push Unassigned to the very bottom
      if (a.deptName === UNASSIGNED_LABEL) return 1;
      if (b.deptName === UNASSIGNED_LABEL) return -1;
      return a.deptName.localeCompare(b.deptName);
    }
    if (a.fromPrev !== b.fromPrev) return a.fromPrev ? -1 : 1;
    return a.segStart - b.segStart || (a.emp.name || "").localeCompare(b.emp.name || "");
  });
  return segs;
}

// counts[48] for a list of segments, respecting long-lunch gaps.
function countBuckets(segments) {
  const arr = new Array(48).fill(0);
  for (const s of segments) {
    for (let i = 0; i < 48; i++) {
      const a = i * 30, b = a + 30;
      if (b <= s.segStart || a >= s.segEnd) continue;
      if (s.longLunch && a >= s.longLunch.start && b <= s.longLunch.end) continue;
      arr[i] += 1;
    }
  }
  return arr;
}

// Merge contiguous runs (same predicate) into intervals for the caution strip.
// predicate(i) returns a "kind" string or null.
function mergeRuns(len, predicate) {
  const runs = {};
  let cur = null;
  for (let i = 0; i < len; i++) {
    const kind = predicate(i);
    if (kind && cur && cur.kind === kind) { cur.end = (i + 1) * 30; continue; }
    if (cur) (runs[cur.kind] = runs[cur.kind] || []).push(cur);
    cur = kind ? { kind, start: i * 30, end: (i + 1) * 30 } : null;
  }
  if (cur) (runs[cur.kind] = runs[cur.kind] || []).push(cur);
  return runs;
}

// Compute expected headcount per department for the selected iso, from the
// master template.  Returns { [deptName]: number[48] }.
function expectedByDept({ iso, masterTemplate, codeMap, payPeriodStart }) {
  if (!iso || !masterTemplate || !payPeriodStart) return {};
  const anchor = parseISO(payPeriodStart);
  const target = parseISO(iso);
  const idx = differenceInCalendarDays(target, anchor);
  if (idx < 0) return {};
  const days = masterTemplate?.days || [];
  if (idx >= days.length) return {};
  const todayCodes = days[idx] || [];
  const prevCodes = idx > 0 ? (days[idx - 1] || []) : [];
  const out = {};
  const add = (deptName, buckets) => {
    if (!out[deptName]) out[deptName] = new Array(48).fill(0);
    buckets.forEach(b => { if (b >= 0 && b < 48) out[deptName][b] += 1; });
  };
  for (const code of todayCodes) {
    const meta = codeMap?.get(code); if (!meta) continue;
    if (meta.type !== "work") continue;
    const deptName = (meta.department || "").trim() || UNASSIGNED_LABEL;
    add(deptName, bucketsForCode(meta, 0));
  }
  for (const code of prevCodes) {
    const meta = codeMap?.get(code); if (!meta) continue;
    if (meta.type !== "work") continue;
    const deptName = (meta.department || "").trim() || UNASSIGNED_LABEL;
    add(deptName, bucketsForCode(meta, -1));
  }
  return out;
}

export default function DayTimelineDialog({
  open, onClose, iso, employees, roster, assignments, codes, canEdit, onSaved,
  masterTemplate, payPeriodStart,
}) {
  const codeMap = useMemo(() => {
    const m = new Map();
    (codes || []).forEach(c => m.set(c.code, c));
    return m;
  }, [codes]);

  const baseSegments = useMemo(
    () => segmentsForDay({ iso, assignments, employees, roster, codeMap }),
    [iso, assignments, employees, roster, codeMap]
  );

  // Local pending edits keyed by segment.key
  const [edits, setEdits] = useState({});   // { key: {segStart, segEnd} }
  useEffect(() => { setEdits({}); }, [iso, open]);

  const segments = useMemo(() => baseSegments.map(s => {
    const e = edits[s.key];
    return e ? { ...s, segStart: e.segStart, segEnd: e.segEnd } : s;
  }), [baseSegments, edits]);

  // Grouped by department (preserves the sorted order from segmentsForDay).
  const deptGroups = useMemo(() => {
    const groups = [];
    const byName = new Map();
    for (const s of segments) {
      if (!byName.has(s.deptName)) {
        const g = { name: s.deptName, meta: s.deptMeta, segments: [] };
        byName.set(s.deptName, g); groups.push(g);
      }
      byName.get(s.deptName).segments.push(s);
    }
    return groups;
  }, [segments]);

  const totalCounts = useMemo(() => countBuckets(segments), [segments]);
  const peak = Math.max(0, ...totalCounts);

  // Global "no one scheduled anywhere" runs (across all departments).
  const zeroCoverageRuns = useMemo(() => {
    return (mergeRuns(48, (i) => totalCounts[i] === 0 ? "z" : null).z) || [];
  }, [totalCounts]);

  const expected = useMemo(
    () => expectedByDept({ iso, masterTemplate, codeMap, payPeriodStart }),
    [iso, masterTemplate, codeMap, payPeriodStart]
  );

  // ---- Drag mechanics (pointer events, 10-min snap) ----
  const trackRef = useRef(null);
  const dragRef = useRef(null);

  const onPointerDown = (e, seg, mode) => {
    if (!canEdit || !seg.editable) return;
    e.preventDefault();
    const track = trackRef.current?.getBoundingClientRect();
    if (!track) return;
    dragRef.current = {
      key: seg.key, mode, startX: e.clientX, trackLeft: track.left, trackWidth: track.width,
      origStart: seg.segStart, origEnd: seg.segEnd,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp, { once: true });
  };
  const pxToMin = (dx, trackWidth) => Math.round(((dx / trackWidth) * 1440) / SNAP) * SNAP;
  const onPointerMove = (e) => {
    const d = dragRef.current; if (!d) return;
    const delta = pxToMin(e.clientX - d.startX, d.trackWidth);
    let ns = d.origStart, ne = d.origEnd;
    if (d.mode === "left")  ns = Math.max(0, Math.min(d.origEnd - SNAP, d.origStart + delta));
    if (d.mode === "right") ne = Math.min(1440, Math.max(d.origStart + SNAP, d.origEnd + delta));
    if (d.mode === "body") {
      const dur = d.origEnd - d.origStart;
      ns = Math.max(0, Math.min(1440 - dur, d.origStart + delta));
      ne = ns + dur;
    }
    setEdits(prev => ({ ...prev, [d.key]: { segStart: ns, segEnd: ne } }));
  };
  const onPointerUp = () => {
    dragRef.current = null;
    window.removeEventListener("pointermove", onPointerMove);
  };

  const dirtyKeys = Object.keys(edits);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (dirtyKeys.length === 0) return;
    setSaving(true);
    let ok = 0, err = 0;
    for (const key of dirtyKeys) {
      const seg = baseSegments.find(s => s.key === key);
      const e = edits[key];
      if (!seg || !e) continue;
      try {
        await api.post("/schedule/assign", {
          employee_id: seg.emp.id, date: seg.date, facility_id: seg.facility_id,
          shift_code: seg.code,
          start_override: fmt(e.segStart),
          end_override: fmt(e.segEnd),
          modified_note: "Adjusted from day timeline",
        });
        ok++;
      } catch (ex) { err++; toast.error(formatApiErrorDetail(ex?.response?.data?.detail) || `Failed to save ${seg.emp.name}`); }
    }
    setSaving(false);
    if (ok) toast.success(`Saved ${ok} shift${ok === 1 ? "" : "s"}`);
    if (ok) { onSaved?.(); onClose(); }
    if (!ok && err) return;
  };

  if (!open || !iso) return null;
  const pct = (m) => (m / 1440) * 100;
  const dateObj = parseISO(iso);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-2 sm:p-6" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-6xl max-h-[95vh] sm:max-h-[92vh] shadow-2xl flex flex-col overflow-hidden"
        data-testid="day-timeline"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — shrink-0 so it never scrolls off; close button is always reachable */}
        <div className="p-3 sm:p-5 border-b border-slate-200 flex items-center gap-2 sm:gap-4 shrink-0">
          <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-blue-600 text-white grid place-items-center shrink-0">
            <Users2 className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm sm:text-lg leading-tight truncate">
              {mdyDow(dateObj)}
            </div>
            <div className="text-[11px] sm:text-xs text-slate-500 mt-0.5 truncate">
              <span data-testid="tl-total">{segments.length}</span> shifts · <b data-testid="tl-dept-count">{deptGroups.length}</b> dept{deptGroups.length === 1 ? "" : "s"} · peak <b data-testid="tl-peak">{peak}</b>
              {canEdit && <span className="ml-2 text-slate-400 hidden md:inline">Drag either end of a bar (or the middle) in 10-min steps</span>}
            </div>
          </div>
          {canEdit && dirtyKeys.length > 0 && (
            <Button className="bg-blue-600 hover:bg-blue-700 shrink-0 text-xs sm:text-sm h-8 sm:h-10 px-2 sm:px-3" onClick={save} disabled={saving} data-testid="tl-save">
              <Save size={14} className="sm:mr-1" />
              <span className="hidden sm:inline ml-1">{saving ? "Saving…" : `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}`}</span>
              <span className="sm:hidden ml-1">{saving ? "…" : dirtyKeys.length}</span>
            </Button>
          )}
          <button
            className="shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-full grid place-items-center text-slate-500 hover:text-slate-900 hover:bg-slate-100 active:bg-slate-200 border border-slate-200"
            onClick={onClose}
            aria-label="Close"
            data-testid="tl-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Global no-coverage banner */}
        {zeroCoverageRuns.length > 0 && (
          <div className="px-3 sm:px-5 pt-3 sm:pt-4 pb-1 space-y-1.5 shrink-0" data-testid="tl-nocoverage">
            {zeroCoverageRuns.map((r, i) => (
              <div key={`z-${i}`} className="text-[12px] rounded-md border border-red-200 bg-red-50 text-red-900 px-2.5 py-1 inline-flex items-center gap-1.5 mr-2" data-testid="tl-warn-zero">
                <ShieldAlert size={12} /> <b>Nobody scheduled</b> from {fmt(r.start)}–{fmt(r.end)}
              </div>
            ))}
          </div>
        )}

        {/* Mobile hint — swipe to explore */}
        <div className="sm:hidden px-3 pt-2 pb-1 text-[10px] text-slate-400 flex items-center gap-1 shrink-0">
          <span>← swipe to explore the 24-hour timeline →</span>
        </div>

        {/* Body — min-h-0 + touch-pan so nested overflow-auto actually scrolls on mobile */}
        <div
          className="flex-1 min-h-0 overflow-auto thin-scroll overscroll-contain"
          style={{ WebkitOverflowScrolling: "touch", touchAction: "pan-x pan-y" }}
        >
          <div className="min-w-[900px] p-4">
            {/* Global axis (same width used inside each group) */}
            <div className="flex sticky top-0 z-10 bg-white">
              <div className="w-[200px] shrink-0" />
              <div style={{ width: TIMELINE_WIDTH }} ref={trackRef}>
                <div className="relative h-8 border-b border-slate-200" data-testid="tl-axis">
                  {Array.from({ length: 25 }).map((_, h) => (
                    <div key={`h-${h}`} className="absolute top-0 text-[10px] tabular-nums text-slate-700"
                         style={{ left: `${pct(h * 60)}%` }}>
                      <div className="absolute top-0 left-0 w-px h-3 bg-slate-400" />
                      <div className="absolute top-3 -translate-x-1/2 font-semibold">{String(h).padStart(2, "0")}:00</div>
                    </div>
                  ))}
                  {Array.from({ length: 48 }).map((_, i) => (i % 2 === 1) && (
                    <div key={`m-${i}`} className="absolute top-0 w-px h-2 bg-slate-300" style={{ left: `${pct(i * 30)}%` }} />
                  ))}
                </div>
              </div>
            </div>

            {segments.length === 0 && (
              <div className="text-center text-sm text-slate-500 py-10">Nobody is scheduled on {mdy(dateObj)}.</div>
            )}

            {/* Grouped departments */}
            <div className="mt-3 space-y-4" data-testid="timeline-groups">
              {deptGroups.map((g) => {
                const dc = g.meta;
                const gCounts = countBuckets(g.segments);
                const gPeak = Math.max(0, ...gCounts);
                const gExpected = expected[g.name] || new Array(48).fill(0);

                // Only flag when master template asked for more people than we scheduled.
                const shortRuns  = mergeRuns(48, (i) => {
                  const need = gExpected[i] || 0;
                  return need > 0 && gCounts[i] < need ? `n${need - gCounts[i]}` : null;
                });

                const isUnassigned = g.name === UNASSIGNED_LABEL;

                return (
                  <div key={g.name} className={`rounded-xl border ${dc.border} bg-white`} data-testid={`dept-group-${g.name}`}>
                    {/* Divider / header */}
                    <div className={`flex items-center gap-3 px-3 py-2 rounded-t-xl border-b ${dc.border} ${dc.strip}`}>
                      <span className={`inline-block w-2.5 h-2.5 rounded-full ${dc.dot}`} />
                      <div className={`font-display font-semibold text-sm ${dc.text}`} data-testid={`dept-name-${g.name}`}>
                        {isUnassigned ? (
                          <span className="inline-flex items-center gap-1 text-slate-600">
                            <Building2 size={12} /> Unassigned
                          </span>
                        ) : g.name}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {g.segments.length} shift{g.segments.length === 1 ? "" : "s"} · peak{" "}
                        <b data-testid={`dept-peak-${g.name}`}>{gPeak}</b>
                      </div>
                      <div className="ml-auto flex flex-wrap gap-1 justify-end">
                        {shortRuns && Object.entries(shortRuns).map(([k, runs]) => {
                          const shortBy = parseInt(k.slice(1), 10);
                          return runs.map((r, i) => (
                            <span key={`s-${k}-${i}`} className="text-[11px] rounded-md border border-orange-300 bg-orange-50 text-orange-900 px-2 py-0.5 inline-flex items-center gap-1"
                              data-testid={`dept-short-${g.name}`}>
                              <AlertTriangle size={11} />
                              missing {shortBy} · {fmt(r.start)}–{fmt(r.end)}
                            </span>
                          ));
                        })}
                      </div>
                    </div>

                    {/* Count strip for this department */}
                    <div className="flex">
                      <div className="w-[200px] pr-3 shrink-0 flex items-center text-[10px] uppercase tracking-wider text-slate-400 pl-3">
                        headcount
                      </div>
                      <div style={{ width: TIMELINE_WIDTH }} className="relative h-5">
                        {gCounts.map((c, i) => {
                          const need = gExpected[i] || 0;
                          const short = need > 0 && c < need;
                          return (
                            <div key={i}
                              className={`absolute top-0 text-[10px] tabular-nums text-center ${
                                short ? "text-orange-700 font-semibold"
                                : c === gPeak && gPeak > 0 ? `${dc.text} font-semibold`
                                : "text-slate-500"
                              }`}
                              style={{ left: `${pct(i * 30)}%`, width: `${pct(30)}%` }}
                              title={need > 0 ? `have ${c} · need ${need}` : `have ${c}`}>
                              {c}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Rows for this department */}
                    <div className="pb-2">
                      {g.segments.map((s) => {
                        const meta = codeMap.get(s.code) || { code: s.code, color: "coverage_pink" };
                        const clr = colorForShift(meta);
                        const dirty = !!edits[s.key];
                        const paid = paidHours(s.segStart, s.segEnd);
                        const shorter = dirty && (s.segEnd - s.segStart) < (s.origEnd - s.origStart);
                        const ot = paid > 8;
                        return (
                          <div key={s.key} className="flex items-center" data-testid={`timeline-row-${s.emp.id}-${s.key}`}>
                            <div className="w-[200px] pr-3 flex items-center gap-2 shrink-0 pl-3">
                              <Avatar className="w-7 h-7"><AvatarImage src={s.emp.avatar} /><AvatarFallback className="text-[10px]">{(s.emp.name || "?")[0]}</AvatarFallback></Avatar>
                              <div className="min-w-0">
                                <div className="text-xs font-medium truncate" data-testid={`tl-name-${s.emp.id}`}>{s.emp.name}</div>
                                <div className="text-[9px] text-slate-500 flex items-center gap-1.5">
                                  {s.fromPrev && (<span data-testid={`tl-prevday-${s.emp.id}`} className="inline-flex items-center gap-0.5"><Moon size={9} /> from prev day</span>)}
                                  {dirty && <span className="text-blue-700 font-semibold">edited · {paid}h{ot ? " (OT)" : ""}</span>}
                                  {dirty && shorter && <span className="text-pink-700">needs coverage</span>}
                                  {!dirty && ot && <span className="text-amber-700 font-semibold">OT · {paid}h</span>}
                                </div>
                              </div>
                            </div>
                            <div className="relative h-8 border-t border-b border-slate-100" style={{ width: TIMELINE_WIDTH }}>
                              {Array.from({ length: 48 }).map((_, i) => (
                                <div key={i} className="absolute top-0 bottom-0 w-px bg-slate-100" style={{ left: `${pct(i * 30)}%` }} />
                              ))}
                              <div
                                className={`absolute top-1 bottom-1 rounded-md border text-[10px] font-semibold flex items-center overflow-hidden ${
                                  ot
                                    ? "bg-red-100 border-red-400 text-red-900"
                                    : "bg-sky-100 border-sky-300 text-sky-900"
                                } ${s.fromPrev ? "opacity-80 border-dashed" : ""} ${dirty ? "ring-2 ring-blue-400" : ""} ${canEdit && s.editable ? "cursor-grab active:cursor-grabbing" : ""}`}
                                data-ot={ot ? "true" : "false"}
                                style={{
                                  left: `${pct(s.segStart)}%`,
                                  width: `${pct(s.segEnd - s.segStart)}%`,
                                  minWidth: 24,
                                }}
                                onPointerDown={(e) => canEdit && s.editable && onPointerDown(e, s, "body")}
                                title={`${s.emp.name} · ${s.code} · ${fmt(s.segStart)}–${fmt(s.segEnd)} · ${paid}h${ot ? " (OT)" : ""}${s.fromPrev ? " (from prev day)" : ""}`}
                              >
                                {canEdit && s.editable && (
                                  <div
                                    data-testid={`tl-handle-l-${s.key}`}
                                    className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-black/10"
                                    onPointerDown={(e) => { e.stopPropagation(); onPointerDown(e, s, "left"); }}
                                  />
                                )}
                                <span className="px-1.5 truncate select-none pointer-events-none">
                                  {s.code} · {fmt(s.segStart)}–{fmt(s.segEnd)} · {paid}h
                                </span>
                                {canEdit && s.editable && (
                                  <div
                                    data-testid={`tl-handle-r-${s.key}`}
                                    className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-black/10"
                                    onPointerDown={(e) => { e.stopPropagation(); onPointerDown(e, s, "right"); }}
                                  />
                                )}
                              </div>
                              {s.longLunch && (
                                <div className="absolute top-1 bottom-1 bg-white/95 border border-orange-300 border-dashed"
                                     style={{ left: `${pct(s.longLunch.start)}%`, width: `${pct(s.longLunch.end - s.longLunch.start)}%` }}
                                     title={`Long lunch ${fmt(s.longLunch.start)}–${fmt(s.longLunch.end)}`} />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="border-t border-slate-200 p-3 flex flex-wrap gap-4 text-[11px] text-slate-500">
          <div className="flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm border border-sky-300 bg-sky-100" /> Shift bar</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm border border-red-400 bg-red-100" /> OT (&gt; 8h paid)</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm border border-dashed border-sky-300 bg-sky-100 opacity-80" /> From previous day</div>
          <div className="flex items-center gap-1.5"><span className="inline-block w-4 h-3 rounded-sm border-2 border-blue-500 bg-sky-100" /> Edited (unsaved)</div>
          <div className="flex items-center gap-1.5"><span className="text-orange-700 font-semibold">missing N</span> = below the master template</div>
          <div className="flex items-center gap-1.5"><span className="text-red-800 font-semibold">Nobody scheduled</span> = no coverage anywhere</div>
        </div>
      </div>
    </div>
  );
}
